/**
 * Calendar Header Component
 * Navigation, export, and action buttons for calendar
 */

import React from 'react'
import { Loading } from '@talktoast/ui'
import { ICONS } from '@talktoast/shared'

interface CalendarHeaderProps {
  onBack?: () => void
  onExport: () => void
  onShowMonth: () => void
  onDone: () => void
  canExport: boolean
  canPushToGoogle?: boolean
  onPushToGoogle?: () => void
  isPushingToGoogle?: boolean
}

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  onBack,
  onExport,
  onShowMonth,
  onDone,
  canExport,
  canPushToGoogle = false,
  onPushToGoogle,
  isPushingToGoogle = false
}) => {
  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onBack || (() => window.history.back())}
            className="text-gray-600 hover:text-gray-900 transition-colors text-lg"
          >
            ← Back
          </button>
          <h1 className="text-lg font-bold text-gray-900">Meal Schedule</h1>
          <div className="flex items-center gap-2">
            {canPushToGoogle && onPushToGoogle && (
              <button
                onClick={onPushToGoogle}
                disabled={isPushingToGoogle}
                className="transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-blue-600 hover:text-blue-800"
                title="Push to Google Calendar"
                aria-label={isPushingToGoogle ? 'Pushing to Google Calendar…' : 'Push to Google Calendar'}
              >
                {isPushingToGoogle ? (
                  <Loading variant="spinner" size="sm" />
                ) : (
                  <ICONS.navigation.calendar className="w-5 h-5" />
                )}
              </button>
            )}
            <button
              onClick={onExport}
              disabled={!canExport}
              className={`transition-colors ${
                canExport
                  ? 'text-gray-600 hover:text-gray-900'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              title={canExport ? 'Export to calendar' : 'No meals assigned to export'}
              aria-label={canExport ? 'Export to calendar' : 'No meals assigned to export'}
            >
              <ICONS.actions.download className="w-5 h-5" />
            </button>
            <button
              onClick={onShowMonth}
              className="text-gray-600 hover:text-gray-900 transition-colors"
              title="View full month"
              aria-label="View full month"
            >
              <ICONS.navigation.calendarDays className="w-5 h-5" />
            </button>
            <button
              onClick={onDone}
              className="text-blue-600 hover:text-blue-700 transition-colors font-medium text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onBack || (() => window.history.back())}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            ← Back
          </button>
          <div className="flex items-center gap-3">
            {canPushToGoogle && onPushToGoogle && (
              <button
                onClick={onPushToGoogle}
                disabled={isPushingToGoogle}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Push to Google Calendar"
              >
                {isPushingToGoogle ? (
                  <>
                    <Loading variant="spinner" size="sm" />
                    <span className="font-medium">Pushing...</span>
                  </>
                ) : (
                  <>
                    <GoogleCalendarIcon />
                    <span className="font-medium">Push to Google Calendar</span>
                  </>
                )}
              </button>
            )}
            <button
              onClick={onExport}
              disabled={!canExport}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                canExport
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-50 text-gray-300 cursor-not-allowed'
              }`}
              title={canExport ? 'Export .ics file' : 'No meals assigned to export'}
            >
              <ICONS.actions.download className="w-4 h-4" />
              <span className="font-medium">Export .ics</span>
            </button>
            <button
              onClick={onDone}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Meal Schedule</h1>
      </div>
    </>
  )
}

// Google Calendar Icon Component
const GoogleCalendarIcon: React.FC = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5zm2 4h10v2H7v-2zm0 4h7v2H7v-2z"/>
  </svg>
)
